import { Component, OnInit } from '@angular/core';

import { RouterLink } from '@angular/router';
import { OfferService } from '../../service/offer.service';
import { OfferWithImage } from '../../model/offer-with-image';
import { OfferCardComponent } from '../components/offer-card/offer-card.component';
import { UserService } from '../../service/user.service';
import { User } from '@/model/user';
import { KeycloakService } from 'keycloak-angular';
import { SkeletonLoadingComponent } from '../components/skeleton-loading/skeleton-loading.component';

@Component({
  selector: 'app-company-view',
  imports: [RouterLink, OfferCardComponent, SkeletonLoadingComponent],
  templateUrl: './company-view.component.html',
  styleUrl: './company-view.component.scss',
  standalone: true,
})
export class CompanyViewComponent implements OnInit {
  offers: OfferWithImage[] = [];
  offersPerPage = 99999;
  offset = 0;
  hasMoreOffers = true;
  isModalOpen = false;
  selectedOfferId: string | null = null;
  approvedOffers: OfferWithImage[] = [];
  pendingOffers: OfferWithImage[] = [];
  modificationRequestedOffers: OfferWithImage[] = [];
  currentUser: User | null = null;
  isAdmin = false;
  isLoading = false;

  constructor(
    private offerService: OfferService,
    private userService: UserService,
    private keycloakService: KeycloakService,
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.isAdmin = this.keycloakService.getUserRoles().includes('admin');
    this.userService.getUserInfo().subscribe({
      next: (user) => {
        this.currentUser = user;
        this.loadMoreOffers();
      },
      error: (error) => {
        console.error('Error loading user info:', error);
        this.isLoading = false;
      },
    });
  }

  loadMoreOffers(): void {
    let request = this.offerService.getMyOffers(
      this.offersPerPage,
      this.offset,
    );

    if (this.isAdmin) {
      request = this.offerService.getAllOffersAnyStatus(
        this.offersPerPage,
        this.offset,
      );
    }

    request.subscribe({
      next: (data: OfferWithImage[]) => {
        this.offers = [...this.offers, ...data];
        // Remove duplicate offers based on id
        this.offers = this.offers.filter(
          (offer, index, self) =>
            index === self.findIndex((o) => o.id === offer.id),
        );

        // Sort offers by updatedDate in descending order (newest first)
        this.offers.sort(
          (a, b) => b.updatedDate.getTime() - a.updatedDate.getTime(),
        );

        this.approvedOffers = this.offers.filter(
          (offer) => offer.status === 'APPROVED',
        );
        this.pendingOffers = this.offers.filter(
          (offer) => offer.status === 'NEW',
        );
        this.modificationRequestedOffers = this.offers.filter(
          (offer) => offer.status === 'REQUEST_MODIFICATION',
        );

        this.offset += this.offersPerPage;
        this.hasMoreOffers = data.length === this.offersPerPage;
        this.isLoading = false;
      },
      error: (error: Error) => {
        console.error('Fehler beim Laden der Angebote:', error);
        this.isLoading = false;
      },
    });
  }

  getStatusDetails(status: string): { text: string; colorClass: string } {
    switch (status) {
      case 'REJECTED':
        return { text: 'abgelehnt', colorClass: 'bg-red-500' };
      case 'APPROVED':
        return { text: 'bestätigt', colorClass: 'bg-sondisGreen' };
      case 'NEW':
      case 'REVISED':
      case 'REQUEST_MODIFICATION':
        return { text: 'wird bearbeitet', colorClass: 'bg-gray-500' };
      default:
        return { text: 'wird bearbeitet', colorClass: 'bg-gray-500' };
    }
  }

  openDeleteModal(offerId: string): void {
    this.selectedOfferId = offerId;
    this.isModalOpen = true;
  }

  closeDeleteModal(): void {
    this.isModalOpen = false;
    this.selectedOfferId = null;
  }

  confirmDelete(): void {
    if (this.selectedOfferId) {
      this.offerService.deleteOffer(this.selectedOfferId).subscribe({
        next: () => {
          this.offers = this.offers.filter(
            (offer) => offer.id !== this.selectedOfferId,
          );

          this.closeDeleteModal();
        },
        error: (error) => {
          console.error('Fehler beim Löschen des Angebots:', error);
        },
        complete: () => {
          console.log('Löschen des Angebots erfolgreich');
        },
      });
    }
  }

  handleOfferDeleted(offerId: string): void {
    this.offers = this.offers.filter((offer) => offer.id !== offerId);
    this.approvedOffers = this.approvedOffers.filter(
      (offer) => offer.id !== offerId,
    );
    this.pendingOffers = this.pendingOffers.filter(
      (offer) => offer.id !== offerId,
    );
  }

  stripHtmlTags(description: string): string {
    return description.replace(/<[^>]*>/g, '');
  }
}
